/**
 * S3DragDropZone.tsx
 * 
 * Portal-based drag-drop overlay component with visual feedback and file/folder support.
 * 
 * Features:
 * - Full-screen overlay rendered via React Portal
 * - Drag event handling (dragenter, dragover, dragleave, drop)
 * - Visual feedback with CSS border glow on valid drop
 * - Only enabled for authenticated users
 * - Delegates to validation and folder traversal logic
 * - Cross-browser support with feature detection
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import * as ReactDOM from 'react-dom';
import { useS3Upload } from '../../hooks/useS3Upload';
import { useS3Browser } from '../../contexts/S3BrowserContext';
import { useAuth } from '../../contexts/AuthContext';
import { validateFileBatch } from '../../utils/fileValidator';
import { traverseFolderEntries } from '../../utils/folderEntryWalker';
import { getDragDropCapabilities, logDragDropCapabilities } from '../../utils/browserCompatibility';
import type { UploadOptions } from '../../types/s3';
import './S3DragDropZone.css';

/**
 * Props for S3DragDropZone component
 */
export interface S3DragDropZoneProps {
  /** Current S3 path where files will be uploaded */
  currentPath?: string;
  /** Optional upload options for validation */
  uploadOptions?: UploadOptions;
  /** Callback fired when valid files are dropped */
  onFilesDropped?: (files: File[]) => void;
  /** Callback fired when folders are dropped (requires folder traversal) */
  onFoldersDropped?: (folders: DataTransferItem[]) => void;
  /** Callback fired when invalid items are dropped */
  onInvalidItems?: (items: DataTransferItem[]) => void;
  /** Whether the component is enabled (default: true) */
  isEnabled?: boolean;
  /** Whether user is authenticated */
  isAuthenticated?: boolean;
}

/**
 * S3DragDropZone Component
 * 
 * Renders a full-screen overlay that appears during drag operations and accepts
 * dropped files and folders. Uses React Portal to render at document body level.
 */
export const S3DragDropZone: React.FC<S3DragDropZoneProps> = ({
  currentPath = '',
  uploadOptions,
  onFilesDropped,
  onFoldersDropped,
  onInvalidItems,
  isEnabled = true,
  isAuthenticated: isAuthenticatedProp,
}) => {
  // Get authentication status from AuthContext
  const { currentUser } = useAuth();
  const isAuthenticated = isAuthenticatedProp !== undefined ? isAuthenticatedProp : !!currentUser;
  
  // Try to get current path from S3BrowserContext if available (for drag-drop into current folder)
  // This allows the drag-drop zone to be context-aware when used within S3 browser pages
  let contextPath: string | null = null;
  try {
    const browserContext = useS3Browser();
    contextPath = browserContext?.state?.navigation?.currentPath || null;
  } catch (error) {
    // Not in S3BrowserProvider context - that's okay, use prop instead
    contextPath = null;
  }
  
  // Use context path if available, otherwise fall back to prop
  const effectiveCurrentPath = contextPath !== null ? contextPath : currentPath;
  
  console.log('[S3DragDropZone] Using path for uploads:', {
    propPath: currentPath,
    contextPath,
    effectivePath: effectiveCurrentPath,
  });
  
  // State management
  const [isDragging, setIsDragging] = useState(false);
  const [isValidDropTarget, setIsValidDropTarget] = useState(false);
  
  // Ref to track overlay element
  const overlayRef = useRef<HTMLDivElement>(null);
  
  // Ref to count drag events for handling nested dragenter/dragleave
  const dragCountRef = useRef(0);
  
  // Ref for browser capabilities (set once on component mount)
  const capabilitiesRef = useRef(getDragDropCapabilities());
  
  // Upload context with cache invalidation support
  const { startUploadWithCacheInvalidation } = useS3Upload();

  /**
   * Handle dragenter event - increment drag counter and show overlay
   */
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCountRef.current++;
    
    // Only show overlay if we have dragged files
    const hasFiles = e.dataTransfer?.types?.includes('Files') ?? false;
    if (hasFiles) {
      setIsDragging(true);
      // Check if it's a valid drop target
      const isValid = hasFiles && isEnabled && isAuthenticated;
      setIsValidDropTarget(isValid);
    }
  }, [isEnabled, isAuthenticated]);

  /**
   * Handle dragover event - prevent default and set visual feedback
   */
  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Visual feedback only for valid drops
    const isValid = e.dataTransfer?.types?.includes('Files') ?? false;
    setIsValidDropTarget(isValid && isEnabled && isAuthenticated);
  }, [isEnabled, isAuthenticated]);

  /**
   * Handle dragleave event - decrement drag counter and hide overlay when appropriate
   */
  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCountRef.current--;
    
    // Only hide overlay when all drag sessions have ended
    if (dragCountRef.current === 0) {
      setIsDragging(false);
      setIsValidDropTarget(false);
    }
  }, []);

  /**
   * Process dropped files or folders
   */
  const processDroppedItems = useCallback(
    async (transferItems: DataTransferItem[]) => {
      const allFiles: File[] = [];
      const invalid: DataTransferItem[] = [];
      
      try {
        // Use folder traversal utility to handle both files and folders
        const traversalResult = await traverseFolderEntries(transferItems, {
          uploadOptions,
          onProgress: (message: string) => {
            // Could log progress or update UI
            console.debug('Folder traversal progress:', message);
          },
        });
        
        // Collect files from traversal
        if (traversalResult.files.length > 0) {
          allFiles.push(...traversalResult.files);
        }
        
        // Log any traversal errors
        if (traversalResult.traversalErrors.length > 0) {
          console.warn('Folder traversal errors:', traversalResult.traversalErrors);
        }
        
        // Process collected files
        if (allFiles.length > 0) {
          // Validate all files together
          const validationResult = validateFileBatch(allFiles, uploadOptions);
          
          if (validationResult.isValid) {
            // Start upload job with all validated files and automatic cache invalidation
            await startUploadWithCacheInvalidation(allFiles, effectiveCurrentPath, uploadOptions);
            
            // Invoke callback if provided
            if (onFilesDropped) {
              onFilesDropped(allFiles);
            }
          } else if (validationResult.errors.length > 0) {
            // Some files failed validation
            console.warn('File validation errors:', validationResult.errors);
            
            // Report invalid files
            if (onInvalidItems) {
              // Map files back to transfer items where possible
              const invalidItems = allFiles.map((f) => {
                // Try to find corresponding transfer item
                for (const item of transferItems) {
                  if (item.kind === 'file') {
                    const itemFile = item.getAsFile();
                    if (itemFile && itemFile.name === f.name && itemFile.size === f.size) {
                      return item;
                    }
                  }
                }
                return null;
              }).filter((item): item is DataTransferItem => item !== null);
              
              if (invalidItems.length > 0) {
                onInvalidItems(invalidItems);
              }
            }
          }
        }
        
        // Report any items that couldn't be processed
        if (invalid.length > 0 && onInvalidItems) {
          onInvalidItems(invalid);
        }
      } catch (error) {
        console.error('Error processing dropped items:', error);
        
        // Report error as invalid items
        if (onInvalidItems) {
          onInvalidItems(transferItems);
        }
      }
    },
    [uploadOptions, effectiveCurrentPath, startUploadWithCacheInvalidation, onFilesDropped, onInvalidItems]
  );

  /**
   * Handle drop event - process dropped items
   */
  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Reset drag state
      dragCountRef.current = 0;
      setIsDragging(false);
      setIsValidDropTarget(false);
      
      // Only process if enabled and authenticated
      if (!isEnabled || !isAuthenticated) {
        return;
      }
      
      // Extract items from data transfer
      const items = e.dataTransfer?.items;
      if (!items || items.length === 0) {
        return;
      }
      
      // Convert DataTransferItemList to array
      const itemsArray = Array.from(items);
      
      // Process dropped items
      processDroppedItems(itemsArray);
    },
    [isEnabled, isAuthenticated, processDroppedItems]
  );

  /**
   * Log browser capabilities on component mount
   */
  useEffect(() => {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      logDragDropCapabilities();
    }
  }, []);

  /**
   * Set up event listeners on window/document
   */
  useEffect(() => {
    // Only set up listeners if enabled and authenticated
    if (!isEnabled || !isAuthenticated) {
      return;
    }
    
    // Add listeners to window to capture drag events
    window.addEventListener('dragenter', handleDragEnter as EventListener);
    window.addEventListener('dragover', handleDragOver as EventListener);
    window.addEventListener('dragleave', handleDragLeave as EventListener);
    window.addEventListener('drop', handleDrop as EventListener);
    
    return () => {
      window.removeEventListener('dragenter', handleDragEnter as EventListener);
      window.removeEventListener('dragover', handleDragOver as EventListener);
      window.removeEventListener('dragleave', handleDragLeave as EventListener);
      window.removeEventListener('drop', handleDrop as EventListener);
    };
  }, [handleDragEnter, handleDragOver, handleDragLeave, handleDrop, isEnabled, isAuthenticated]);

  /**
   * Reset drag state on component unmount
   */
  useEffect(() => {
    return () => {
      dragCountRef.current = 0;
      setIsDragging(false);
      setIsValidDropTarget(false);
    };
  }, []);

  // Don't render overlay if not dragging
  if (!isDragging) {
    return null;
  }

  // Get portal container (document body)
  const portalContainer = document.body;
  
  // Determine message based on browser capabilities
  const supportsFolders = capabilitiesRef.current.supportsFolderDrops;
  const message = isValidDropTarget
    ? supportsFolders
      ? 'Drop files or folders to upload'
      : 'Drop files to upload (folders not supported in this browser)'
    : supportsFolders
    ? 'Drag valid files or folders'
    : 'Drag valid files';
  
  // Create overlay element
  const overlayClasses = [
    's3-drag-drop-overlay',
    isValidDropTarget ? 's3-drag-drop-overlay--valid' : 's3-drag-drop-overlay--invalid',
  ].join(' ');

  // Render via React Portal
  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className={overlayClasses}
      data-testid="s3-drag-drop-overlay"
      role="region"
      aria-label="Drag and drop zone"
    >
      <div className="s3-drag-drop-overlay__content">
        <div className="s3-drag-drop-overlay__icon">
          {isValidDropTarget ? '📁' : '⛔'}
        </div>
        <p className="s3-drag-drop-overlay__text">
          {message}
        </p>
      </div>
    </div>,
    portalContainer
  );
};

export default S3DragDropZone;
